'use client';
import type { NavigationItemUI } from '@/logic/page-config/navigation';
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useStore } from '@/store/store';
import { BottomNavItem } from '@/app/components/bottom-bar/subcomponents/bottom-nav-item';
import type { GameLaunchPayload } from '@/logic/game-info/game-info';
import { useGlobalContext } from '@/store/contexts/global-context';
import { isPageRewardMachineInfo, isPageRewardsMachineExitScreen } from '@/utils/reward-machine/constants';
import { ROUTES } from '@/utils/constants';
import { ErrorBoundary } from '../error-boundary/error-boundary';

type BottomBarProps = {
    navItems: Array<NavigationItemUI>;
    hasNewGames: boolean;
    gameLaunchDataByUid?: Record<string, GameLaunchPayload>;
};
const isMomentumRoute = (currentRoute: string) => currentRoute.startsWith(ROUTES.MOMENTUM);
const isChallengeDetailsRoute = (currentRoute: string) => currentRoute.startsWith(ROUTES.CHALLENGE_DETAILS);
export const BottomBarClient = ({ navItems, hasNewGames, gameLaunchDataByUid }: BottomBarProps) => {
    const { setShowNewGames, hideBottomBar } = useStore((state) => state.uiToggles);
    const { isMobile, isIosWrapper } = useGlobalContext();
    const pathname = usePathname() ?? '/';
    const isMachineExitScreenOrRewardMachineInfoPage =
        isIosWrapper && (isPageRewardsMachineExitScreen(pathname) || isPageRewardMachineInfo(pathname));
    const shouldHideBottomBar =
        isMachineExitScreenOrRewardMachineInfoPage ||
        isMomentumRoute(pathname) ||
        isChallengeDetailsRoute(pathname) ||
        hideBottomBar;

    useEffect(() => {
        setShowNewGames(hasNewGames);
    }, [hasNewGames]);

    if (!isMobile || shouldHideBottomBar) {
        return null;
    }

    return (
        <div className="bottom-bar">
            <ErrorBoundary name="bottom-bar">
                {navItems.map((item) => (
                    <BottomNavItem
                        key={item.pathname}
                        {...item}
                        gameLaunchData={item.linkUID ? gameLaunchDataByUid?.[item.linkUID] : undefined}
                    />
                ))}
            </ErrorBoundary>
        </div>
    );
};
